import { Button, Group, Menu, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import React, { useEffect, useState } from 'react';
import { IconFolderPlus, IconUpload } from '@tabler/icons-react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { AudioPlayer } from 'src/meetings/detail/tabs/transcript/AudioPlayer';
import { ContentPaneOverlay } from 'src/UI/app-container/ContentPaneOverlay';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { EntityWithFile, UploadedFile } from '../hooks/file-uploads/types';
import { useFileUploads } from '../hooks/file-uploads/useFileUploads';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import { useDocumentAudioPlayer } from '../hooks/useDocumentAudioPlayer';
import { useUpdateFileDocumentDate } from 'src/data-capture/hooks/useUpdateFileDocumentDate';
import { isAudioFile } from './allowedFileTypes';
import FileUploadLayout from './FileUploadLayout';
import DocumentsFileCard from './components/DocumentsFileCard';
import FolderStripItem from './components/FolderStripItem';
import { CreateFolderModal } from './modals/CreateFolderModal';
import { RenameFolderModal } from './modals/RenameFolderModal';
import MoveFileModal from './files-table/MoveFileModal';

const HIGHLIGHT_DURATION_MS = 3000;

export default function FilesPage() {
  const { sectionSlug, folderId } = useParams<{ sectionSlug?: string; folderId?: string }>();

  const currentFolder =
    folderId || (sectionSlug?.startsWith('folder/') ? sectionSlug.replace('folder/', '') : null);
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId, client } = useCurrentClient();
  const [searchValue, setSearchValue] = useState('');
  const [createFolderOpened, { open: openCreateFolder, close: closeCreateFolder }] =
    useDisclosure(false);
  const [selectedFileForMove, setSelectedFileForMove] = useState<UploadedFile | null>(null);
  const [moveModalOpened, { open: openMoveModal, close: closeMoveModal }] = useDisclosure(false);
  const [selectedFolderForRename, setSelectedFolderForRename] = useState<UploadedFile | null>(null);
  const [renameModalOpened, { open: openRenameModal, close: closeRenameModal }] =
    useDisclosure(false);
  const [fileToDelete, setFileToDelete] = useState<UploadedFile | null>(null);
  const [highlightedFileId, setHighlightedFileId] = useState<string | null>(null);

  const {
    files,
    allOtherFiles,
    deleteFile,
    deleteError,
    resetDeleteError,
    isDeleting,
    createFolder,
    moveFile,
    renameFolder,
    isLoading: isLoadingFiles,
    error,
    refetchFiles,
  } = useFileUploads({
    autoRefreshProcessing: true,
  });
  const isLoading = isLoadingFiles;

  const { updateDocumentDate } = useUpdateFileDocumentDate();

  useEffect(() => {
    refetchFiles();
  }, [sectionSlug, folderId]);

  useEffect(() => {
    const highlightFileId = (location.state as { highlightFileId?: string } | null)
      ?.highlightFileId;
    if (!highlightFileId) return;

    setHighlightedFileId(highlightFileId);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state]);

  useEffect(() => {
    if (!highlightedFileId) return;
    const timer = setTimeout(() => setHighlightedFileId(null), HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [highlightedFileId]);

  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();

  const handleDeletePress = (file: UploadedFile) => {
    resetDeleteError();
    setFileToDelete(file);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    try {
      await deleteFile(fileToDelete.id);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete file',
        'An error occurred while trying to delete the file. Please try again later.',
        e,
        { clientId, fileId: fileToDelete.id },
      );
    }
    setFileToDelete(null);
  };

  const audioPlayer = useDocumentAudioPlayer();

  const handleDownloadFile = async (uploadedFile: UploadedFile, anchor?: HTMLAnchorElement) => {
    try {
      const link = await createLink(uploadedFile.file.id);
      if (anchor) {
        anchor.href = link.url;
        anchor.download = uploadedFile.file.fileName || 'download';
        anchor.target = '_blank';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } else {
        window.open(link.url, '_blank');
      }
    } catch (error) {
      notifyAndLogError(
        'Failed to download file',
        'An error occurred while trying to download the file. Please try again later.',
        error,
        { clientId, fileId: uploadedFile.file.id },
      );
    }
  };

  const handleFolderClick = (folder: EntityWithFile) => {
    navigate(`/client/${clientId}/document/files/folder/${folder.id}`);
  };

  const handleFileDocument = (file: UploadedFile) => {
    setSelectedFileForMove(file);
    openMoveModal();
  };

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { clientId, fileId: file.id, parentId },
      );
    }
  };

  const getCurrentFolderName = () => {
    if (!currentFolder) {
      return `All ${client?.firstName || 'client'}'s documents`;
    }

    const folder = files.find(f => f.id === currentFolder && f.isFolder);
    return folder?.file.fileName || 'Unknown folder';
  };

  const handleCreateFolder = async (folderName: string) => {
    try {
      await createFolder(folderName, currentFolder ?? undefined);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to create folder',
        'An error occurred while trying to create the folder. Please try again later.',
        error,
        { clientId, folderName, currentFolder },
      );
      throw error;
    }
  };

  const handleRenameFolderClick = (folder: UploadedFile) => {
    setSelectedFolderForRename(folder);
    openRenameModal();
  };

  const handleRenameFolder = async (folder: UploadedFile, newName: string) => {
    await renameFolder(folder, newName);
    await refetchFiles();
  };

  const handleUpdateDocumentDate = async (file: UploadedFile, date: Date) => {
    try {
      await updateDocumentDate(
        clientId,
        file.id,
        date,
        file.isResolved,
        file.parentId,
        file.file.isFiled,
      );
    } catch (error) {
      notifyAndLogError(
        'Failed to update date',
        'An error occurred while trying to update the date. Please try again later.',
        error,
        { clientId, dataUploadFileId: file.id },
      );
    }
  };

  const handleUploadClick = () => {
    navigate(`/client/${clientId}/document/add`);
  };

  const filesByFolder = allOtherFiles.reduce(
    (acc: { [parentId: string]: UploadedFile[] }, uploadedFile) => {
      const key = uploadedFile.parentId || 'root';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(uploadedFile);
      return acc;
    },
    {},
  );

  const currentFolderFiles = filesByFolder?.[currentFolder || 'root'] || [];
  const filteredFiles = searchValue
    ? currentFolderFiles.filter(file =>
        file.file.fileName.toLowerCase().includes(searchValue.toLowerCase()),
      )
    : currentFolderFiles;

  if (error || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isLoading || isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  const folders = filteredFiles.filter(f => f.isFolder);
  const documents = filteredFiles
    .filter(f => !f.isFolder)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const buildMenuItems = (file: UploadedFile) => {
    if (file.isFolder) {
      return (
        <>
          <Menu.Item onClick={() => handleRenameFolderClick(file)}>Rename</Menu.Item>
          <Menu.Item onClick={() => handleFileDocument(file)}>Move</Menu.Item>
          <Menu.Item onClick={() => handleDeletePress(file)}>Delete</Menu.Item>
        </>
      );
    }

    if (isAudioFile(file.file.fileType, file.file.fileName)) {
      return (
        <>
          <Menu.Item onClick={() => audioPlayer.download(file, clientId)}>
            Download document
          </Menu.Item>
          <Menu.Item onClick={() => audioPlayer.play(file.file.id, file.file.fileName)}>
            Play
          </Menu.Item>
          <Menu.Item onClick={() => handleFileDocument(file)}>Move</Menu.Item>
          <Menu.Item onClick={() => handleDeletePress(file)}>Delete</Menu.Item>
        </>
      );
    }

    return (
      <>
        <Menu.Item onClick={() => handleDownloadFile(file, document.createElement('a'))}>
          Download document
        </Menu.Item>
        <Menu.Item onClick={() => handleFileDocument(file)}>Move</Menu.Item>
        <Menu.Item onClick={() => handleDeletePress(file)}>Delete</Menu.Item>
      </>
    );
  };

  return (
    <FileUploadLayout
      title={getCurrentFolderName()}
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchPlaceholder="Search documents"
      actions={
        <Group gap="sm" wrap="nowrap">
          <Button
            variant="secondary"
            size="md"
            onClick={openCreateFolder}
            aria-label="Create folder"
            title="Create folder"
            px="xs"
          >
            <IconFolderPlus size={16} />
          </Button>
          <Button
            variant="primary"
            size="md"
            leftSection={<IconUpload size={16} />}
            onClick={handleUploadClick}
          >
            Upload
          </Button>
        </Group>
      }
    >
      <Stack gap="xxl">
        {folders.length > 0 && (
          <Group gap="sm" wrap="wrap">
            {folders.map(folder => (
              <FolderStripItem
                key={folder.id}
                folder={folder}
                onOpen={handleFolderClick}
                menuItems={buildMenuItems(folder)}
              />
            ))}
          </Group>
        )}

        <Stack gap="xxs">
          {documents.map(file => (
            <DocumentsFileCard
              key={file.id}
              file={file}
              highlighted={file.id === highlightedFileId}
              menuItems={buildMenuItems(file)}
              onUpdateDate={handleUpdateDocumentDate}
            />
          ))}
        </Stack>
      </Stack>

      <CreateFolderModal
        opened={createFolderOpened}
        onClose={closeCreateFolder}
        onCreateFolder={handleCreateFolder}
      />

      {selectedFileForMove && (
        <MoveFileModal
          opened={moveModalOpened}
          close={closeMoveModal}
          file={selectedFileForMove}
          folders={files}
          rootLabel={`All ${client?.firstName || 'client'}'s documents`}
          onFileMoved={handleMoveFile}
        />
      )}

      {selectedFolderForRename && (
        <RenameFolderModal
          opened={renameModalOpened}
          onClose={closeRenameModal}
          folder={selectedFolderForRename}
          onRename={handleRenameFolder}
        />
      )}

      <ConfirmDeleteModal
        opened={!!fileToDelete}
        onClose={() => setFileToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={fileToDelete?.isFolder ? 'Delete folder' : 'Delete file'}
        message={`Are you sure you want to delete "${fileToDelete?.file.fileName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeleting}
      />

      {audioPlayer.isVisible && (
        <ContentPaneOverlay>
          <AudioPlayer
            isPlaying={audioPlayer.isPlaying}
            currentTime={audioPlayer.currentTime}
            duration={audioPlayer.duration}
            isMuted={audioPlayer.isMuted}
            onPlayPause={audioPlayer.handlePlayPause}
            onSeek={audioPlayer.handleSeek}
            onMuteToggle={audioPlayer.handleMuteToggle}
            fileName={audioPlayer.fileName}
            onClose={audioPlayer.handleClose}
          />
        </ContentPaneOverlay>
      )}
    </FileUploadLayout>
  );
}
